"use client";

import type React from "react";
import { useCallback, useState } from "react";
import { HighGasPriceModal } from "@/components/core/HighGasPriceModal";
import { HIGH_GAS_THRESHOLD_GWEI, useGasPriceCheck } from "./useGasPriceCheck";

interface UseGasPriceGuardOptions {
  thresholdGwei?: bigint;
}

interface UseGasPriceGuardReturn {
  /**
   * Wraps an async function and checks gas price before executing.
   * If gas is high, shows a warning modal and waits for user confirmation.
   */
  executeWithGasCheck: <T>(fn: () => Promise<T>) => Promise<T | null>;
  /**
   * The modal component to render in your component tree.
   */
  GasPriceGuardModal: () => React.ReactNode;
  /**
   * Whether the gas price is currently being checked.
   */
  isCheckingGas: boolean;
  /**
   * Whether the guarded function is currently executing.
   */
  isExecuting: boolean;
}

export const useGasPriceGuard = (
  options?: UseGasPriceGuardOptions,
): UseGasPriceGuardReturn => {
  const thresholdGwei = options?.thresholdGwei ?? HIGH_GAS_THRESHOLD_GWEI;
  const { checkGasPrice, isChecking } = useGasPriceCheck(thresholdGwei);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFn, setPendingFn] = useState<(() => Promise<unknown>) | null>(
    null,
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  const executeWithGasCheck = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        const result = await checkGasPrice();

        if (result.isHighGas) {
          // Store the function and show modal
          setPendingFn(() => fn);
          setIsModalOpen(true);

          // Return a promise that resolves when user makes a choice
          return new Promise<T | null>((resolve, reject) => {
            setResolvePromise({
              resolve: resolve as (value: unknown) => void,
              reject,
            });
          });
        }

        // Gas is not high, execute immediately
        setIsExecuting(true);
        const fnResult = await fn();
        setIsExecuting(false);
        return fnResult;
      } catch (error) {
        setIsExecuting(false);
        throw error;
      }
    },
    [checkGasPrice],
  );

  const handleProceed = useCallback(async () => {
    if (!pendingFn || !resolvePromise) return;

    setIsExecuting(true);
    try {
      const result = await pendingFn();
      resolvePromise.resolve(result);
    } catch (error) {
      resolvePromise.reject(error);
    } finally {
      setIsExecuting(false);
      setIsModalOpen(false);
      setPendingFn(null);
      setResolvePromise(null);
    }
  }, [pendingFn, resolvePromise]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise.resolve(null);
    }
    setIsModalOpen(false);
    setPendingFn(null);
    setResolvePromise(null);
  }, [resolvePromise]);

  const GasPriceGuardModal = useCallback(() => {
    if (!isModalOpen) return null;

    return (
      <HighGasPriceModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onProceed={handleProceed}
        onCancel={handleCancel}
        thresholdGwei={thresholdGwei.toString()}
        isLoading={isExecuting}
      />
    );
  }, [isModalOpen, handleProceed, handleCancel, isExecuting, thresholdGwei]);

  return {
    executeWithGasCheck,
    GasPriceGuardModal,
    isCheckingGas: isChecking,
    isExecuting,
  };
};
